"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import DataTable from "../DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import DocumentPreviewButton from "./DocumentPreviewButton";

export type JournalRow = {
  id?: string;
  date: string;
  documentNo?: string;
  ref: string;
  text: string;
  debit: string;
  credit: string;
  amount: string;
  status: "PROVISORISCH" | "FINAL";
  aiConfidence?: number | null;
  lockBy?: string | null;
  lockUntil?: string | null;
};

function renderStatus(row: JournalRow) {
  const SAFE_CONFIDENCE_THRESHOLD = 0.9;
  if (row.status === "FINAL") {
    return <span className="pill pill-final">FINAL</span>;
  }
  if (
    typeof row.aiConfidence === "number" &&
    Number.isFinite(row.aiConfidence) &&
    row.aiConfidence >= SAFE_CONFIDENCE_THRESHOLD
  ) {
    return <span className="pill pill-review">{(row.aiConfidence * 100).toFixed(1)} % sicher</span>;
  }
  return <span className="pill pill-provisional">PROVISORISCH</span>;
}

function FinalizeButton({ row }: { row: JournalRow }) {
  const router = useRouter();
  const [busy, setBusy] = React.useState(false);

  if (row.status === "FINAL" || !row.id) {
    return <span style={{ color: "var(--muted)" }}>-</span>;
  }

  return (
    <button
      type="button"
      className="btn btn-ghost"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await fetch("/api/journal/finalize", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ entryId: row.id }),
          });
          router.refresh();
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? "..." : "Finalisieren"}
    </button>
  );
}

const columns: ColumnDef<JournalRow>[] = [
  { header: "Datum", accessorKey: "date" },
  { header: "Beleg", accessorKey: "documentNo" },
  { header: "Ref", accessorKey: "ref" },
  { header: "Text", accessorKey: "text" },
  { header: "Soll", accessorKey: "debit" },
  { header: "Haben", accessorKey: "credit" },
  { header: "Betrag", accessorKey: "amount" },
  {
    header: "Status",
    accessorKey: "status",
    cell: (info) => renderStatus(info.row.original)
  },
  {
    header: "Beleg",
    id: "document",
    cell: (info) => <DocumentPreviewButton entryId={info.row.original.id} documentNo={info.row.original.documentNo} />
  },
  {
    header: "Aktion",
    id: "action",
    cell: (info) => <FinalizeButton row={info.row.original} />
  }
];

export default function JournalTable({ data }: { data: JournalRow[] }) {
  const router = useRouter();
  const [selected, setSelected] = React.useState<Record<string, boolean>>({});
  const [bulkBusy, setBulkBusy] = React.useState(false);

  React.useEffect(() => {
    setSelected({});
  }, [data]);

  const selectableIds = React.useMemo(
    () => data.filter((r) => r.id && r.status !== "FINAL").map((r) => r.id as string),
    [data]
  );
  const selectedIds = React.useMemo(
    () => selectableIds.filter((id) => selected[id]),
    [selectableIds, selected]
  );
  const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;

  const columnsWithSelect = React.useMemo<ColumnDef<JournalRow>[]>(() => {
    return [
      {
        id: "select",
        header: () => (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => {
              const next: Record<string, boolean> = {};
              if (e.target.checked) {
                for (const id of selectableIds) next[id] = true;
              }
              setSelected(next);
            }}
            title="Alle offenen markieren"
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          if (!row.id || row.status === "FINAL") return <span style={{ color: "var(--muted)" }}>-</span>;
          return (
            <input
              type="checkbox"
              checked={!!selected[row.id]}
              onChange={(e) => {
                setSelected((prev) => ({ ...prev, [row.id as string]: e.target.checked }));
              }}
            />
          );
        }
      },
      ...columns
    ];
  }, [allSelected, selectableIds, selected]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ color: "var(--muted)", fontSize: 13 }}>
          Markiert: <strong>{selectedIds.length}</strong>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={selectedIds.length === 0 || bulkBusy}
          onClick={async () => {
            setBulkBusy(true);
            try {
              await fetch("/api/journal/finalize", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ entryIds: selectedIds })
              });
              router.refresh();
            } finally {
              setBulkBusy(false);
            }
          }}
        >
          {bulkBusy ? "Finalisiert..." : "Markierte finalisieren"}
        </button>
      </div>
      <DataTable<JournalRow> columns={columnsWithSelect} data={data} />
    </div>
  );
}
